import { z } from 'zod';
import { subscriptionBillingCadenceSchema } from '../../partner-management/partner-subscription-management/subscriptionBillingCadence.js';
import { subscriptionOrderSummarySchema } from '../../partner-management/partner-subscription-management/subscriptionOrderSummary.js';
import { subscriptionPaymentMethodSchema } from '../../partner-management/partner-subscription-management/subscriptionPaymentMethod.js';
import { subscriptionPlanCatalogueEntrySchema } from '../../partner-management/partner-subscription-management/subscriptionPlanCatalogue.js';
import { PARTNER_COCKPIT_MAX_NAME_LENGTH, partnerCockpitLanguageSchema } from '../user-management/auth.js';
export const onboardingSubscriptionPlanSchema = subscriptionPlanCatalogueEntrySchema.omit({ id: true });
export const onboardingSubscriptionPlansResponseSchema = z.object({
    plans: onboardingSubscriptionPlanSchema.array(),
});
const invoiceContactNameSchema = z
    .string()
    .trim()
    .max(PARTNER_COCKPIT_MAX_NAME_LENGTH)
    .transform((value) => value || undefined)
    .optional();
export const onboardingBillingAddressSchema = z
    .object({
    companyName: z.string().trim().min(1).max(200),
    firstName: invoiceContactNameSchema,
    lastName: invoiceContactNameSchema,
    billingEmail: z.email().max(320),
    street: z.string().trim().min(1).max(200),
    nr: z.string().trim().max(20),
    zip: z.string().trim().min(1).max(20),
    city: z.string().trim().min(1).max(100),
})
    // Half a name cannot fall back to the account owner without naming two people on one invoice.
    .superRefine(({ firstName, lastName }, ctx) => {
    if (Boolean(firstName) === Boolean(lastName)) {
        return;
    }
    ctx.addIssue({
        code: 'custom',
        path: [firstName ? 'lastName' : 'firstName'],
        message: 'Name the contact person in full or leave both names empty',
    });
});
export const selectOnboardingSubscriptionSchema = z.object({
    planUuid: z.uuid(),
    billingCadence: subscriptionBillingCadenceSchema,
    paymentMethod: subscriptionPaymentMethodSchema,
});
export const onboardingSubscriptionOrderStateSchema = z.enum([
    'AwaitingPlanSelection',
    'AwaitingConfirmation',
    // Card and TWINT only
    'AwaitingPaymentDetails',
    'Confirmed',
]);
export const OnboardingSubscriptionOrderState = onboardingSubscriptionOrderStateSchema.enum;
export const onboardingSubscriptionOrderStateWithOrderSchema = z.enum([
    onboardingSubscriptionOrderStateSchema.enum.AwaitingConfirmation,
    onboardingSubscriptionOrderStateSchema.enum.AwaitingPaymentDetails,
    onboardingSubscriptionOrderStateSchema.enum.Confirmed,
]);
export const onboardingSubscriptionOrderWithPlanSchema = subscriptionOrderSummarySchema.extend({
    state: onboardingSubscriptionOrderStateWithOrderSchema,
    planUuid: z.uuid(),
    selectablePaymentMethods: subscriptionPaymentMethodSchema.array(),
    // What the invoice form edits, seeded from the company step.
    invoiceDetails: onboardingBillingAddressSchema.nullable(),
    // Who the invoice will name: the contact entered at checkout, or the account owner. Null off the invoice path.
    invoiceContactName: z.string().nullable(),
});
export const onboardingSubscriptionOrderSchema = z.discriminatedUnion('state', [
    z.object({ state: z.literal(onboardingSubscriptionOrderStateSchema.enum.AwaitingPlanSelection) }),
    onboardingSubscriptionOrderWithPlanSchema,
]);
export const confirmOnboardingSubscriptionSchema = z.object({
    termsAccepted: z.literal(true),
    language: partnerCockpitLanguageSchema.optional(),
    // Required on the invoice path and rejected on the others.
    invoiceDetails: onboardingBillingAddressSchema.optional(),
});
export const confirmOnboardingSubscriptionResultSchema = z.object({
    // Null on the invoice path, which collects nothing and completes on the acceptance of the ToS.
    redirectUrl: z.string().nullable(),
});
